import HubLeadCTA from "@/components/HubLeadCTA";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { VISA_PRODUCTS, VISA_PRODUCT_CATEGORIES, VISA_CATALOG_PRICE_MIN, VISA_CATALOG_PRICE_MAX } from "@/data/visa-products";
import { BRAND_URL } from "@/lib/brand";
import { ProductCatalogHubNav } from "@/components/ProductCatalogHubNav";

const TITLE = `บริการยื่นวีซ่าไทย ${VISA_PRODUCTS.length} ประเภท | Non-B, LTR, DTV, Elite, O-A · NYC Legal`;
const DESC = `แคตตาล็อกครบทุกวีซ่าที่ ${VISA_PRODUCTS.length} ประเภท — Non-B, LTR 10 ปี, Smart, DTV, Elite, Retirement O-A/O-X, Marriage, ED, Tourist · ปรึกษาฟรี.`;

export const Route = createFileRoute("/visa/products/")({
  head: () => {
    const url = `${BRAND_URL}/visa/products`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/visa/products/index", "th")}, { name: "description", content: ctaDesc(DESC, "/visa/products/index", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/visa/products/index", "th")}, { property: "og:description", content: ctaDesc(DESC, "/visa/products/index", "th")},
        { property: "og:url", content: url }, { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "en", href: `${BRAND_URL}/en/visa/products` },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [{
            "@type": "CollectionPage", "@id": `${url}#collection`,
            url, name: TITLE, inLanguage: "th-TH",
            numberOfItems: VISA_PRODUCTS.length,
          }, {
            "@type": "ItemList", "@id": `${url}#itemlist`,
            itemListElement: VISA_PRODUCTS.map((p, i) => ({
              "@type": "ListItem", position: i + 1,
              url: `${BRAND_URL}/visa/products/${p.slug}`, name: p.nameTh,
            })),
          }, {
            "@type": "OfferCatalog", "@id": `${url}#catalog`, name: TITLE,
            itemListElement: VISA_PRODUCTS.map((p) => ({
              "@type": "Offer", name: p.nameTh, sku: p.sku,
              priceCurrency: "THB", price: p.priceMid,
              url: `${BRAND_URL}/visa/products/${p.slug}`,
            })),
          }],
        }),
      }],
    };
  },
  component: CatalogTH,
});

function CatalogTH() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-sky-100/60 to-background px-4 pt-16 pb-10">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-sky-500/15 px-3 py-1 text-xs font-bold text-sky-700">Visa Product Catalog</span>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl">บริการยื่นวีซ่าไทย — {VISA_PRODUCTS.length} ประเภท</h1>
          <p data-speakable className="direct-answer mt-4 max-w-3xl rounded-xl border-l-4 border-sky-400 bg-sky-50/50 p-4 text-[15px] leading-relaxed text-slate-700">
            ค่าบริการ ฿{VISA_CATALOG_PRICE_MIN.toLocaleString()}–{VISA_CATALOG_PRICE_MAX.toLocaleString()} · ประเมินคุณสมบัติฟรีภายใน 24 ชม. · ที่ปรึกษาด้าน BOI / ตม. / กรมการกงสุล — ยื่นให้ครบตั้งแต่เอกสารถึงติดตามผลอนุมัติ
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-10">
        {VISA_PRODUCT_CATEGORIES.map((c) => (
          <div key={c.id} className="mb-10">
            <h2 className="mb-4 font-display text-xl font-extrabold text-navy-deep">
              {c.labelTh} <span className="text-sm font-normal text-slate-500">({c.count} ประเภท)</span>
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {VISA_PRODUCTS.filter((p) => p.category === c.id).map((p) => (
                <Link key={p.sku} to="/visa/products/$slug" params={{ slug: p.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-sky-400 hover:shadow-md">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">{p.visa.code}</div>
                  <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-sky-700">{p.nameTh}</div>
                  <div className="mt-2 text-xs text-slate-600 line-clamp-2">{p.shortDescTh}</div>
                  <div className="mt-3 text-xs font-semibold text-emerald-700">฿{p.priceMin.toLocaleString()}–{p.priceMax.toLocaleString()} · {p.turnaround}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
      <ProductCatalogHubNav locale="th" current="visa" />
    <HubLeadCTA locale="th" service="Visa Service" />
      </main>
  );
}
